import { format } from 'date-fns';
import { ARTEFACT_META } from '../constants';
import {
  ArtefactType,
  CaseArtefact,
  CaseOverviewStructured,
  GlossaryStructured,
  IssuesStructured,
  OpenQuestionsStructured,
  PartiesStructured,
  TimelineStructured,
} from '../types';
import { stripMarkdown } from './artefactMeta';
import { DashboardConfidenceDisplay, getConfidenceDisplay } from './confidence';

export type DashboardTabId = 'action-items' | 'issues' | 'timeline' | 'parties' | 'glossary';

export interface DashboardTab {
  id: DashboardTabId;
  label: string;
}

export interface DashboardMetricModel {
  generatedAt: string | null;
  documentCount: number;
  openQuestionCount: number;
  conflictCount: number;
  timelineRange: {
    earliest: string | null;
    latest: string | null;
  } | null;
}

export interface DashboardTheory {
  title: string;
  summary: string;
  party: string;
  supportingAllegations: string[];
  supportingDocuments: string[];
  weaknesses: string[];
}

export interface DashboardWeakness {
  text: string;
  supportingDocuments: string[];
}

export interface DashboardQuestion {
  question: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  category: string | null;
  suggestedAction: string | null;
  relatedEntities: string[];
  relatedFiles: string[];
}

export interface DashboardGuidanceGroup {
  priority: Extract<DashboardQuestion['priority'], 'critical' | 'high' | 'medium'>;
  items: string[];
}

export interface DashboardModel {
  overview: {
    artefact: CaseArtefact | null;
    data: CaseOverviewStructured | null;
    summary: string;
    confidence: DashboardConfidenceDisplay;
    theories: DashboardTheory[];
    weaknesses: DashboardWeakness[];
    metrics: DashboardMetricModel;
  };
  questions: {
    artefact: CaseArtefact | null;
    items: DashboardQuestion[];
    guidanceGroups: DashboardGuidanceGroup[];
    visibleQuestions: DashboardQuestion[];
    secondaryQuestions: DashboardQuestion[];
  };
  issues: {
    artefact: CaseArtefact | null;
    data: IssuesStructured | null;
  };
  timeline: {
    artefact: CaseArtefact | null;
    data: TimelineStructured | null;
  };
  parties: {
    artefact: CaseArtefact | null;
    data: PartiesStructured | null;
  };
  glossary: {
    artefact: CaseArtefact | null;
    data: GlossaryStructured | null;
  };
  tabs: DashboardTab[];
}

function getArtefactIndex(artefacts: CaseArtefact[]) {
  return artefacts.reduce(
    (acc, artefact) => {
      acc[artefact.artefactType] = artefact;
      return acc;
    },
    {} as Partial<Record<ArtefactType, CaseArtefact>>,
  );
}

function getStructuredData<T>(artefact: CaseArtefact | null): T | null {
  if (!artefact?.structured) {
    return null;
  }

  return artefact.structured as unknown as T;
}

function dedupeWeaknesses(items: DashboardWeakness[]) {
  const seen = new Set<string>();

  return items.filter((item) => {
    const normalized = item.text.trim();
    if (!normalized || seen.has(normalized)) {
      return false;
    }

    seen.add(normalized);
    return true;
  });
}

function formatTimelineRange(
  timeline: TimelineStructured | null,
): DashboardMetricModel['timelineRange'] {
  if (!timeline?.dateRanges?.earliest && !timeline?.dateRanges?.latest) {
    return null;
  }

  return {
    earliest: timeline.dateRanges?.earliest || null,
    latest: timeline.dateRanges?.latest || null,
  };
}

function extractSummary(
  overviewArtefact: CaseArtefact | null,
  overview: CaseOverviewStructured | null,
) {
  if (overview?.summary) {
    return overview.summary;
  }

  if (!overviewArtefact?.content) {
    return '';
  }

  const executiveSummaryMatch = overviewArtefact.content.match(
    /##\s+Executive Summary\s*([\s\S]*?)(?:\n##\s+|$)/i,
  );
  const text = executiveSummaryMatch?.[1] || overviewArtefact.content;
  const paragraph = text
    .split(/\n{2,}/)
    .map((section) => stripMarkdown(section))
    .find(Boolean);

  return paragraph || '';
}

function parseQuestions(data: OpenQuestionsStructured | null): DashboardQuestion[] {
  if (!data?.questions?.length) {
    return [];
  }

  return data.questions.map((question) => ({
    question: question.question,
    priority: question.priority,
    category: question.category || null,
    suggestedAction: question.suggestedAction || null,
    relatedEntities: question.relatedEntities || [],
    relatedFiles: question.relatedFiles || [],
  }));
}

function isActionablePriority(priority: DashboardQuestion['priority']) {
  return priority === 'critical' || priority === 'high';
}

function isSecondaryPriority(priority: DashboardQuestion['priority']) {
  return priority === 'medium' || priority === 'low';
}

function groupQuestionsByPriority(
  questions: DashboardQuestion[],
  priorities: DashboardQuestion['priority'][],
) {
  return priorities.flatMap((priority) =>
    questions.filter((question) => question.priority === priority),
  );
}

function buildGuidanceGroups(questions: DashboardQuestion[]): DashboardGuidanceGroup[] {
  const priorities: DashboardGuidanceGroup['priority'][] = ['critical', 'high', 'medium'];

  return priorities
    .map((priority) => ({
      priority,
      items: questions
        .filter((question) => question.priority === priority)
        .map((question) => question.suggestedAction?.trim())
        .filter((action): action is string => Boolean(action)),
    }))
    .filter((group) => group.items.length > 0);
}

function formatDateRangeLabel(range: { earliest: string | null; latest: string | null } | null) {
  if (!range?.earliest || !range?.latest) {
    return null;
  }

  try {
    return `${format(new Date(range.earliest), 'd MMM yyyy')} - ${format(
      new Date(range.latest),
      'd MMM yyyy',
    )}`;
  } catch {
    return `${range.earliest} - ${range.latest}`;
  }
}

export function getTimelineRangeLabel(model: DashboardModel) {
  return formatDateRangeLabel(model.overview.metrics.timelineRange);
}

export function buildDashboardModel(artefacts: CaseArtefact[]): DashboardModel {
  const sortedArtefacts = [...artefacts].sort(
    (a, b) =>
      (ARTEFACT_META[a.artefactType]?.order ?? 99) - (ARTEFACT_META[b.artefactType]?.order ?? 99),
  );
  const index = getArtefactIndex(sortedArtefacts);

  const overviewArtefact = index['case-overview'] || null;
  const overview = getStructuredData<CaseOverviewStructured>(overviewArtefact);
  const questionsArtefact = index['open-questions'] || null;
  const questions = getStructuredData<OpenQuestionsStructured>(questionsArtefact);
  const issuesArtefact = index.issues || null;
  const issues = getStructuredData<IssuesStructured>(issuesArtefact);
  const timelineArtefact = index.timeline || null;
  const timeline = getStructuredData<TimelineStructured>(timelineArtefact);
  const partiesArtefact = index['parties-and-players'] || null;
  const parties = getStructuredData<PartiesStructured>(partiesArtefact);
  const glossaryArtefact = index['glossary-and-aliases'] || null;
  const glossary = getStructuredData<GlossaryStructured>(glossaryArtefact);

  const parsedQuestions = parseQuestions(questions);
  const actionableQuestions = groupQuestionsByPriority(
    parsedQuestions.filter((question) => isActionablePriority(question.priority)),
    ['critical', 'high'],
  );
  const secondaryQuestions = groupQuestionsByPriority(
    parsedQuestions.filter((question) => isSecondaryPriority(question.priority)),
    ['medium', 'low'],
  );
  const guidanceGroups = buildGuidanceGroups(actionableQuestions);
  const theoryWeaknesses = dedupeWeaknesses(
    (overview?.caseTheories || []).flatMap((theory) =>
      (theory.weaknesses || []).map((weakness) => ({
        text: weakness,
        supportingDocuments: theory.supportingDocuments || [],
      })),
    ),
  );
  const weaknesses =
    theoryWeaknesses.length > 0
      ? theoryWeaknesses
      : actionableQuestions.map((question) => ({
          text: question.question,
          supportingDocuments: question.relatedFiles || [],
        }));

  const tabs: DashboardTab[] = [];

  if (parsedQuestions.length > 0) {
    tabs.push({ id: 'action-items', label: 'Action Items' });
  }

  if (issuesArtefact) {
    tabs.push({ id: 'issues', label: 'Key Issues' });
  }

  if (timelineArtefact) {
    tabs.push({ id: 'timeline', label: 'Timeline' });
  }

  if (parties?.parties?.length || parties?.relationships?.length) {
    tabs.push({ id: 'parties', label: 'Parties & Relationships' });
  }

  if (glossary?.terms?.length || glossary?.abbreviations?.length) {
    tabs.push({ id: 'glossary', label: 'Glossary' });
  }

  return {
    overview: {
      artefact: overviewArtefact,
      data: overview,
      summary: extractSummary(overviewArtefact, overview),
      confidence: getConfidenceDisplay(overviewArtefact?.confidence),
      theories: (overview?.caseTheories || []).slice(0, 3).map((theory) => ({
        title: theory.title,
        summary: theory.coreProposition,
        party: theory.party,
        supportingAllegations: theory.supportingAllegations || [],
        supportingDocuments: theory.supportingDocuments || [],
        weaknesses: theory.weaknesses || [],
      })),
      weaknesses,
      metrics: {
        generatedAt:
          overviewArtefact?.generatedAt ||
          sortedArtefacts[sortedArtefacts.length - 1]?.generatedAt ||
          null,
        documentCount: overviewArtefact?.sourcedFrom?.fileIds?.length || 0,
        openQuestionCount: parsedQuestions.length,
        conflictCount: overviewArtefact?.confidence?.conflicts?.length || 0,
        timelineRange: formatTimelineRange(timeline),
      },
    },
    questions: {
      artefact: questionsArtefact,
      items: parsedQuestions,
      guidanceGroups,
      visibleQuestions: actionableQuestions,
      secondaryQuestions,
    },
    issues: {
      artefact: issuesArtefact,
      data: issues,
    },
    timeline: {
      artefact: timelineArtefact,
      data: timeline
        ? {
            ...timeline,
            events: [...(timeline.events || [])].sort((a, b) => a.date.localeCompare(b.date)),
          }
        : null,
    },
    parties: {
      artefact: partiesArtefact,
      data: parties,
    },
    glossary: {
      artefact: glossaryArtefact,
      data: glossary,
    },
    tabs,
  };
}
